'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCommonData } from '@udemy-v2/common-data';
import { LocalizedHtml, useI18n } from '@udemy-v2/i18n';
import { Button } from '@udemy-v2/react-core-components';
import { FooterButtons, Modal } from '@udemy-v2/react-dialog-components';
import { FormGroup, TextInput } from '@udemy-v2/react-form-components';
import { AlertBanner } from '@udemy-v2/react-notice-components';
import { buildSignupPopupUrl } from '@/app/lib/auth/signup-popup-url';
import { CourseAccessQueryParam } from './constants';
import { CourseLimitedAccess } from './course-limited-access.react-component';
import styles from './password-protected-course.module.scss';
import classNames from 'classnames';
interface CoursePasswordFormProps {
  courseTitle?: string;
  error?: string;
}

/**
 * Form component for entering a course password in a modal. Displays an optional error message and a heading.
 */
export function CoursePasswordForm(t0) {
  const $ = _c(23);
  const {
    courseTitle,
    error
  } = t0;
  const {
    gettext,
    interpolate
  } = useI18n();
  let t1;
  if ($[0] !== courseTitle || $[1] !== gettext || $[2] !== interpolate) {
    t1 = courseTitle ? <LocalizedHtml html={interpolate(gettext("Enter password for: <em class=\"title\">%(title)s</em>"), {
      title: courseTitle
    }, true)} interpolate={{
      title: <em />
    }} /> : gettext("Enter password");
    $[0] = courseTitle;
    $[1] = gettext;
    $[2] = interpolate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const passwordLabel = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = classNames("ud-heading-lg", styles.title);
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== gettext) {
    t3 = gettext("Course password");
    $[5] = gettext;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <h2 id="course-password-title" className={t2}>{t3}</h2>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== error) {
    t5 = error && <AlertBanner udStyle="error" title={error} showCta={false} className={styles["alert-banner"]} />;
    $[9] = error;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TextInput type="password" name="password" autoComplete="current-password" />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== passwordLabel) {
    t7 = <FormGroup label={passwordLabel} className={styles["form-group"]}>{t6}</FormGroup>;
    $[12] = passwordLabel;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== gettext) {
    t8 = gettext("View course");
    $[14] = gettext;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <FooterButtons><Button type="submit">{t8}</Button></FooterButtons>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t7 || $[21] !== t9) {
    t10 = <form method="get">{t4}{t5}{t7}{t9}</form>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t7;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
interface PasswordProtectedCourseProps {
  courseTitle?: string;
  error?: string;
}
export function PasswordProtectedCourse(t0) {
  const $ = _c(18);
  const {
    courseTitle,
    error
  } = t0;
  const {
    gettext,
    locale
  } = useI18n();
  const commonData = useCommonData();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(!!error);
  const isLoggedIn = !!commonData?.user;
  let t1;
  if ($[0] !== isLoggedIn || $[1] !== locale || $[2] !== pathname || $[3] !== searchParams) {
    t1 = function handleButtonClick() {
      if (!isLoggedIn) {
        const password = searchParams?.get(CourseAccessQueryParam.PASSWORD) ?? undefined;
        window.location.assign(buildSignupPopupUrl({
          locale,
          nextPath: pathname ?? "/",
          password: password || undefined
        }));
        return;
      }
      setIsModalOpen(true);
    };
    $[0] = isLoggedIn;
    $[1] = locale;
    $[2] = pathname;
    $[3] = searchParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleButtonClick = t1;
  let t2;
  if ($[5] !== gettext) {
    t2 = gettext("Enter password");
    $[5] = gettext;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsModalOpen(false);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== courseTitle || $[9] !== error) {
    t4 = <CoursePasswordForm courseTitle={courseTitle} error={error} />;
    $[8] = courseTitle;
    $[9] = error;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isModalOpen || $[12] !== t4) {
    t5 = <Modal title="" renderTitle={_temp} isOpen={isModalOpen} onClose={t3}>{t4}</Modal>;
    $[11] = isModalOpen;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== handleButtonClick || $[15] !== t2 || $[16] !== t5) {
    t6 = <CourseLimitedAccess buttonLabel={t2} onButtonClick={handleButtonClick}>{t5}</CourseLimitedAccess>;
    $[14] = handleButtonClick;
    $[15] = t2;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function _temp() {
  return ["course-password-title", null];
}